import { Controller } from "@hotwired/stimulus";

export class ApplicationFilterController extends Controller {
  static targets = ["dateFrom", "dateTo", "presetSelect", "dateTypeSelect", "searchButton"];

  connect() {
    this.applyPreset();
    this.updateSearchAvailability();
  }

  changePreset() {
    this.applyPreset();
  }

  // 日付の種類が未選択のときは検索を実行できないよう、検索ボタンを無効化する
  updateSearchAvailability() {
    this.searchButtonTarget.disabled = this.dateTypeSelectTarget.value === "";
  }

  applyPreset() {
    const preset = this.presetSelectTarget.value;
    if (!preset || preset === "custom") {
      this.enableManualInput();
    } else {
      this.applyPresetRange(preset);
    }
  }

  // 手動入力モード（期間指定）: 日付欄を編集可にする
  enableManualInput() {
    [this.dateFromTarget, this.dateToTarget].forEach((el) => {
      el.readOnly = false;
      this.forceRepaint(el);
    });
  }

  // モバイル（iOS Safari / Android）は <input type="date"> の readonly 解除を再描画しても、
  // read-only のグレー背景が残ってしまうため、一瞬だけ非表示にして強制的に再描画させる。
  forceRepaint(el) {
    el.style.display = "none";
    void el.offsetHeight; // リフローを強制して read-only 解除を反映させる
    el.style.display = "";
  }

  // プリセットモード: 日付欄をロックして計算した範囲を自動入力する
  applyPresetRange(preset) {
    const [fromDate, toDate] = this.calculateDateRange(preset);
    this.dateFromTarget.readOnly = true;
    this.dateToTarget.readOnly = true;
    this.dateFromTarget.value = fromDate;
    this.dateToTarget.value = toDate;
  }

  // プリセット名から日付範囲 [fromDate, toDate] を "YYYY-MM-DD" 文字列で返す（<input type="date"> 用）。
  // 例: 'today' → ['2026-06-09', '2026-06-09'] / 'this_month' → 月初〜月末 / 該当なし → ['', '']
  calculateDateRange(preset) {
    const today = new Date();
    // ローカルTZ準拠で "YYYY-MM-DD" を返す（'sv-SE' ロケールがこの形式）
    const formatDate = (d) => d.toLocaleDateString("sv-SE");
    const addDays = (d, n) => {
      const c = new Date(d);
      c.setDate(c.getDate() + n);
      return c;
    };
    const startOfMonth = (d, m = 0) => new Date(d.getFullYear(), d.getMonth() + m, 1);
    const endOfMonth = (d, m = 0) => new Date(d.getFullYear(), d.getMonth() + m + 1, 0);
    const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
    const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);

    const ranges = {
      yesterday: () => [addDays(today, -1), addDays(today, -1)],
      today: () => [today, today],
      last_7_days: () => [addDays(today, -6), today],
      this_month: () => [startOfMonth(today), endOfMonth(today)],
      last_month: () => [startOfMonth(today, -1), endOfMonth(today, -1)],
      this_year: () => [startOfYear(today), endOfYear(today)],
    };

    const range = ranges[preset]?.() ?? [null, null];
    return range.map((d) => (d ? formatDate(d) : ""));
  }

  // turbo機能を使って、ページ全体をリロードせずに絞り込み条件をクリアする。
  reset() {
    const baseUrl = this.element.dataset.baseUrl || window.location.pathname;
    this.element.closest("turbo-frame").src = baseUrl;
  }
}
